import React, { useState } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import { Game } from "../types/models";
import { formatGameDateTime } from "../utils/dateUtils";

interface QuickScoreFormElement extends HTMLFormElement {
  homeScoreField: HTMLInputElement;
  visitorScoreField: HTMLInputElement;
  stateField: HTMLSelectElement;
}

interface QuickScoreModalProps {
  show: boolean;
  game: Game;
  onHide: () => void;
  onSave: (
    gameId: number,
    homeScore: number,
    visitorScore: number,
    state: "scheduled" | "playing" | "completed",
  ) => void;
}

function QuickScoreModal({ show, game, onHide, onSave }: QuickScoreModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = (event: React.FormEvent<QuickScoreFormElement>) => {
    event.preventDefault();
    setErrorMessage("");

    const form = event.currentTarget;
    const homeScoreValue = form.homeScoreField.value.trim();
    const visitorScoreValue = form.visitorScoreField.value.trim();
    const stateValue = form.stateField.value as
      | "scheduled"
      | "playing"
      | "completed";

    // Validation
    if (!homeScoreValue || !visitorScoreValue) {
      setErrorMessage("Both scores are required");
      return;
    }

    const homeScore = parseInt(homeScoreValue);
    const visitorScore = parseInt(visitorScoreValue);

    if (isNaN(homeScore) || isNaN(visitorScore)) {
      setErrorMessage("Please enter valid scores");
      return;
    }

    if (homeScore < 0 || visitorScore < 0) {
      setErrorMessage("Scores cannot be negative");
      return;
    }

    setIsSubmitting(true);

    // Call parent handler
    try {
      onSave(game.id, homeScore, visitorScore, stateValue);
    } catch (error) {
      setErrorMessage("An error occurred while saving scores");
      setIsSubmitting(false);
    }
  };

  const homeTeamName = game.home_team_name || `Team ${game.home_team_id}`;
  const visitorTeamName =
    game.visitor_team_name || `Team ${game.visitor_team_id}`;

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Quick Score Entry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          <strong>
            {visitorTeamName} @ {homeTeamName}
          </strong>
          <br />
          <small className="text-muted">{formatGameDateTime(game.date)}</small>
        </p>

        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

        <Form onSubmit={handleSubmit} id="quickScoreForm">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="visitorScoreField">
                <Form.Label>{visitorTeamName} (Away)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  defaultValue={game.visitor_score ?? ""}
                  placeholder="0"
                  required
                  disabled={isSubmitting}
                  autoFocus
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="homeScoreField">
                <Form.Label>{homeTeamName} (Home)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  defaultValue={game.home_score ?? ""}
                  placeholder="0"
                  required
                  disabled={isSubmitting}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3" controlId="stateField">
            <Form.Label>Status</Form.Label>
            <Form.Select defaultValue={game.state} disabled={isSubmitting}>
              <option value="scheduled">Scheduled</option>
              <option value="playing">Live</option>
              <option value="completed">Completed</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          form="quickScoreForm"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" /> Saving...
            </>
          ) : (
            "Save Scores"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default QuickScoreModal;
